import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Link } from "react-router-dom";
import { useEquipmentList } from "@/modules/equipment/hooks/useEquipment";
import type { Equipment, EquipmentStatus } from "@/modules/equipment/types";
import { EmptyState } from "@/shared/components/EmptyState";
import { StatusTag } from "@/shared/components/StatusTag";
import { useUrlTableState } from "@/shared/hooks/useUrlState";
import { formatTableIndex } from "@/shared/utils/formatTableIndex";
import { tablePagination } from "@/shared/utils/tablePagination";

const STATUS_LABELS: Record<EquipmentStatus, string> = {
	ACTIVE: "Faol",
	MAINTENANCE: "Ta'mirda",
	INACTIVE: "Faol emas",
	RETIRED: "Iste'foda",
};

const STATUS_TONES: Record<EquipmentStatus, "success" | "warning" | "default" | "error"> = {
	ACTIVE: "success",
	MAINTENANCE: "warning",
	INACTIVE: "default",
	RETIRED: "error",
};

interface BrigadeEquipmentTableProps {
	organizationId: string;
	brigadeId: string;
}

/** Read-only list of equipment assigned to the brigade */
export function BrigadeEquipmentTable({ organizationId, brigadeId }: BrigadeEquipmentTableProps) {
	// Pagination lives in the URL (prefixed to avoid collisions with sibling tables)
	const { page, limit, setPage } = useUrlTableState({ prefix: "beq" });

	const { data, isLoading } = useEquipmentList({ organizationId, brigadeId, page, limit });
	const items = data?.data?.items ?? [];
	const total = data?.data?.pagination?.total ?? 0;

	const columns: ColumnsType<Equipment> = [
		{
			title: "№",
			key: "index",
			width: 55,
			align: "center",
			render: (_: unknown, __: unknown, index: number) => formatTableIndex(page, limit, index),
		},
		{
			title: "Nomi",
			key: "name",
			render: (_: unknown, record: Equipment) => (
				<Link to={`/equipment/${record.id}`} className="font-medium">
					{record.name}
				</Link>
			),
		},
		{
			title: "Turi",
			key: "type",
			width: 140,
			render: (_: unknown, record: Equipment) => record.type?.name ?? "—",
		},
		{
			title: "Seriya raqami",
			dataIndex: "serialNumber",
			key: "serialNumber",
			width: 140,
		},
		{
			title: "Davlat raqami",
			dataIndex: "licensePlate",
			key: "licensePlate",
			width: 130,
			render: (plate: string | null) => plate ?? "—",
		},
		{
			title: "Holat",
			dataIndex: "status",
			key: "status",
			width: 110,
			render: (status: EquipmentStatus) => (
				<StatusTag tone={STATUS_TONES[status] ?? "default"}>
					{STATUS_LABELS[status] ?? status}
				</StatusTag>
			),
		},
	];

	return (
		<Table
			columns={columns}
			dataSource={items}
			loading={isLoading}
			rowKey="id"
			size="middle"
			pagination={tablePagination({
				page,
				limit,
				total,
				onChange: setPage,
				sizeChanger: false,
			})}
			locale={{
				emptyText: <EmptyState description="Brigadaga texnika biriktirilmagan" />,
			}}
		/>
	);
}
